import { Button, Grid, Typography } from '@mui/material'
import { useTranslation } from '@contexts/translation'
import ReactCrop, { Crop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { useCallback, useEffect, useRef, useState } from 'react'
import CommonModal from '../Modal/CommonModal'
import UploadFile from '../Upload/UploadFile'
import { LoadingButton } from '@mui/lab'
import DeleteModal from '../DeleteModal'

interface FileWithPreview {
  file?: File
  preview?: string
  default?: string
  path?: string
  fileData?: File
  blob?: any
}

interface CropImageProps {
  open: boolean
  onClose: () => void
  file?: FileWithPreview
  setFile: (file?: FileWithPreview) => void
  saveImage: (image: File | string, isDelete?: boolean) => Promise<void>
  generalInfoPage?: boolean
  modalTitle?: string
}

// Type definition for the dictionary to fix the string | Dictionary error
interface Dictionary {
  [key: string]: string | Dictionary
}

const CropImage = ({
  open,
  onClose,
  file,
  setFile,
  saveImage,
  generalInfoPage = false,
  modalTitle
}: CropImageProps): JSX.Element => {
  const { dictionary } = useTranslation()
  const imgRef = useRef<HTMLImageElement | null>(null)
  const initialCrop: Crop = { unit: '%', width: 50, height: 100, x: 25 }
  const [crop, setCrop] = useState<Crop>(initialCrop)
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null)
  const [croppedImage, setCroppedImage] = useState<File | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [isDelete, setIsDelete] = useState<boolean>(false)

  const onLoad = useCallback((img: HTMLImageElement) => {
    imgRef.current = img
  }, [])

  const getCroppedImg = (
    image: HTMLImageElement,
    crop: Crop,
    fileName: string
  ): Promise<{ croppedImageUrl: string; blob: Blob }> | undefined => {
    if (image && crop.width && crop.height) {
      const canvas = document.createElement('canvas')
      const scaleX = image.naturalWidth / image.width
      const scaleY = image.naturalHeight / image.height
      canvas.width = crop.width
      canvas.height = crop.height
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        return undefined
      }

      ctx.drawImage(
        image,
        (crop.x || 0) * scaleX,
        (crop.y || 0) * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      )

      return new Promise(resolve => {
        canvas.toBlob(blob => {
          if (!blob) {
            return
          }
          const croppedImageUrl = window.URL.createObjectURL(blob)
          resolve({ croppedImageUrl, blob })
        })
      })
    }
    return undefined
  }

  useEffect(() => {
    const manageImage = async () => {
      if (file && file.file && completedCrop) {
        const result = await getCroppedImg(
          imgRef.current as HTMLImageElement,
          crop,
          file?.file.name
        )

        if (result) {
          const { blob } = result
          const _croppedImage = new File([blob], file?.file.name, {
            type: blob.type
          })
          setCroppedImage(_croppedImage)
        }
      }
    }
    manageImage()
  }, [completedCrop, crop, file])

  return (
    <CommonModal
      open={open}
      title={modalTitle}
      onClose={onClose}
      maxWidth={'md'}
    >
      {file &&
        file.preview &&
        (!generalInfoPage || (generalInfoPage && file.path)) && (
          <Grid container item xs={12} textAlign="center" mb={3}>
            <ReactCrop
              src={file.preview}
              onImageLoaded={onLoad}
              crop={crop}
              onChange={(c: Crop) => setCrop(c)}
              onComplete={(c: Crop) => setCompletedCrop(c)}
              minHeight={50}
              minWidth={50}
            />
          </Grid>
        )}

      {file && file.default && (
        <Grid container item xs={12} textAlign="center" mb={3}>
          <img src={file.default} alt="Default preview" />
        </Grid>
      )}

      {file && file.preview && generalInfoPage && !file.path && (
        <Grid container item xs={12} textAlign="center" mb={3}>
          <img src={file.preview} style={{ maxWidth: '400px' }} alt="Preview" />
        </Grid>
      )}

      {!file && (
        <Grid container item xs={12} textAlign="center" mb={3}>
          <UploadFile
            file={file as any} // Type casting to fix the CustomFile error
            setFile={setFile}
            height={'300px'}
            accept="image/*"
          />
        </Grid>
      )}

      <Grid
        container
        item
        xs={12}
        display="flex"
        justifyContent="space-between"
      >
        {((!file?.default && !generalInfoPage) ||
          (!file?.preview && generalInfoPage)) && (
          <Button
            variant="outlined"
            onClick={() => {
              onClose()
            }}
            data-testid="Button-fa657d29-b264-4b24-9a4e-6f8cd757d149"
          >
            <Typography>
              {typeof dictionary.close === 'string'
                ? dictionary.close
                : 'Close'}
            </Typography>
          </Button>
        )}

        {(file?.default || (file?.preview && generalInfoPage)) && (
          <LoadingButton
            loading={loading}
            variant="outlined"
            color="error"
            onClick={() => setIsDelete(true)}
            data-testid="LoadingButton-26785b45-2ffa-4a35-bb3a-65a9af1325f0"
          >
            <Typography>
              {typeof dictionary.delete === 'string'
                ? dictionary.delete
                : 'Delete'}
            </Typography>
          </LoadingButton>
        )}

        {croppedImage &&
          (!generalInfoPage || (generalInfoPage && file?.path)) && (
            <LoadingButton
              disabled={!file}
              loading={loading}
              onClick={async () => {
                if (!generalInfoPage) {
                  setLoading(true)
                  await saveImage(croppedImage)
                  setLoading(false)
                  onClose()
                  setCrop(initialCrop)
                }
                if (generalInfoPage && croppedImage) {
                  setFile({
                    ...croppedImage,
                    fileData: croppedImage,
                    file: croppedImage,
                    preview: URL.createObjectURL(croppedImage) + '#toolbar=0',
                    blob: file
                  })
                  setCrop(initialCrop)
                  onClose()
                }
              }}
              data-testid="LoadingButton-e4fa55fa-da3d-4cfb-8b0e-b07c479452cc"
            >
              <Typography>
                {typeof dictionary.save === 'string' ? dictionary.save : 'Save'}
              </Typography>
            </LoadingButton>
          )}
      </Grid>

      <DeleteModal
        open={isDelete}
        onClose={() => setIsDelete(false)}
        onConfirm={async () => {
          if (!generalInfoPage) {
            setLoading(true)
            await saveImage('', true)
            setLoading(false)
            setIsDelete(false)
          }
          if (generalInfoPage) {
            setFile(undefined)
            setIsDelete(false)
            setCrop(initialCrop)
          }
        }}
        description={
          typeof dictionary.confirm_to_delete === 'string'
            ? dictionary.confirm_to_delete
            : 'Are you sure you want to delete?'
        }
        loading={loading}
      />
    </CommonModal>
  )
}

export default CropImage
